/**
 * JobAssigneesManagerDialog
 *
 * Modal launched from the "Job Assigned To" SelectField so operators
 * can add / rename / remove the names in the picker without a
 * redeploy. Backed by the ``JobAssigneesContext`` which owns the
 * canonical list.
 *
 * Auth: all users can add. Rename + delete are admin-only; the buttons
 * are hidden for non-admin sessions so the UI matches the backend
 * guard.
 */
import React, { useState } from 'react';
import { useJobAssignees } from '../context/JobAssigneesContext';
import { useAuth } from '../context/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { Loader2, Plus, Pencil, Trash2, Check, X } from 'lucide-react';

const ADMIN_EMAILS = new Set(['j.win@gsimaging.com.au']);

export const JobAssigneesManagerDialog = ({ open, onOpenChange }) => {
  const { assignees, createAssignee, renameAssignee, deleteAssignee } = useJobAssignees();
  const { user } = useAuth();
  const isAdmin = !!user && (user.role === 'admin' || ADMIN_EMAILS.has(user.email));

  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState('');

  const handleAdd = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      await createAssignee(name);
      setNewName('');
      toast.success(`Added "${name}"`);
    } catch (e) {
      toast.error(e.message || 'Could not add name');
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (a) => {
    setEditingId(a.assignee_id);
    setEditDraft(a.name);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft('');
  };

  const saveEdit = async () => {
    const next = editDraft.trim();
    if (!next || !editingId) return cancelEdit();
    setBusy(true);
    try {
      await renameAssignee(editingId, next);
      toast.success('Renamed');
      cancelEdit();
    } catch (e) {
      toast.error(e.message || 'Could not rename');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (a) => {
    if (!window.confirm(`Remove "${a.name}" from the Job Assigned To list?\n\nExisting sales records that reference this name keep their value — they'll show as "(existing)" in the dropdown.`)) return;
    setBusy(true);
    try {
      await deleteAssignee(a.assignee_id);
      toast.success(`Removed "${a.name}"`);
    } catch (e) {
      toast.error(e.message || 'Could not remove');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="job-assignees-manager" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Manage &ldquo;Job Assigned To&rdquo; list</DialogTitle>
          <DialogDescription>
            Add, rename, or remove the operators who show up in the dropdown.
            Changes take effect immediately for everyone.
          </DialogDescription>
        </DialogHeader>

        {/* Add row */}
        <div className="flex items-center gap-2">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
            placeholder="New name"
            disabled={busy}
            className="h-9"
            data-testid="job-assignee-new-name"
          />
          <Button
            onClick={handleAdd}
            disabled={busy || !newName.trim()}
            data-testid="job-assignee-add"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
            Add
          </Button>
        </div>

        {/* List */}
        <div className="border rounded-md divide-y max-h-[45vh] overflow-y-auto">
          {(!assignees || assignees.length === 0) && (
            <div className="text-sm text-muted-foreground italic py-6 text-center" data-testid="job-assignees-empty">
              No names yet — add the first one above.
            </div>
          )}
          {assignees.map(a => (
            <div
              key={a.assignee_id}
              className="flex items-center justify-between px-3 py-2 hover:bg-muted/30"
              data-testid={`job-assignee-row-${a.assignee_id}`}
            >
              {editingId === a.assignee_id ? (
                <>
                  <Input
                    value={editDraft}
                    onChange={(e) => setEditDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveEdit();
                      if (e.key === 'Escape') cancelEdit();
                    }}
                    className="h-8 flex-1 mr-2"
                    autoFocus
                    data-testid={`job-assignee-edit-input-${a.assignee_id}`}
                  />
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      onClick={saveEdit}
                      disabled={busy || !editDraft.trim()}
                      className="h-7"
                      data-testid={`job-assignee-save-${a.assignee_id}`}
                    >
                      <Check className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={cancelEdit}
                      className="h-7"
                    >
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <span className="text-sm font-medium">{a.name}</span>
                  <div className="flex items-center gap-1">
                    {isAdmin ? (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => startEdit(a)}
                          className="h-7 text-muted-foreground hover:text-foreground"
                          title="Rename"
                          data-testid={`job-assignee-edit-${a.assignee_id}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(a)}
                          className="h-7 text-destructive hover:bg-destructive/10"
                          title="Remove"
                          data-testid={`job-assignee-delete-${a.assignee_id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    ) : (
                      <span className="text-[11px] text-muted-foreground italic">Admin edits only</span>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="job-assignees-close"
          >
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default JobAssigneesManagerDialog;
